import { safeGet } from '../../../core/utils/safe-get.js';
import type {
  AnyXYChartScale,
  MapDatapoints,
  RectDatapointInternal,
  XYChartColorScale,
} from '../../types/xy-chart-internals.js';
import { getColorFromScaleAndData } from '../../utils/coloring/get-color-from-scale-and-data.js';
import {
  isCategoricalRectScale,
  isNumericalRectScale,
  isTimeRectScale,
} from '../../utils/type-guards.js';

export function colorAccessor(
  colorScale: XYChartColorScale,
  metaDataMap: MapDatapoints,
) {
  return (datapoint: RectDatapointInternal) =>
    // At this point we know value can not be null, undefined nor NaN, hence it will not be painted in the canvas
    getColorFromScaleAndData(
      colorScale,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      datapoint.value!,
      safeGet(metaDataMap, datapoint)?.colorOverride,
    );
}

function genericAccessor(
  scale: AnyXYChartScale,
  v0: string | number | Date,
  v1?: string | number | Date | undefined,
  relAccessor?: (value: number) => number,
): number {
  const args = { scale, v0, v1 };

  if (isCategoricalRectScale(args)) {
    return args.scale(args.v0) ?? 0;
  } else if (isNumericalRectScale(args) || isTimeRectScale(args)) {
    const { scale } = args;
    let { v0, v1 } = args;

    if (relAccessor) {
      if (isNumericalRectScale(args)) {
        v0 = relAccessor(v0 as number);
        v1 = relAccessor(v1 as number);
      }
    }
    return Math.min(scale(v0), scale(v1));
  }

  throw Error('unknown genericAccessor value for Rect');
}

function spanAccessor(
  scale: AnyXYChartScale,
  v0: string | number | Date,
  v1?: string | number | Date | undefined,
  relAccessor?: (value: number) => number,
): number {
  const args = { scale, v0, v1 };

  if (isNumericalRectScale(args) || isTimeRectScale(args)) {
    const { scale } = args;
    let { v0, v1 } = args;

    if (relAccessor) {
      if (isNumericalRectScale(args)) {
        v0 = relAccessor(v0 as number);
        v1 = relAccessor(v1 as number);
      }
    }
    return Math.abs(scale(v1) - scale(v0));
  } else if (isCategoricalRectScale(args)) {
    return args.scale.bandwidth();
  }

  throw Error('unknown spanAccessor value for Rect');
}

export function xAccessor(
  scale: AnyXYChartScale,
  relAccessor?: (value: number) => number,
) {
  return (datapoint: RectDatapointInternal) =>
    genericAccessor(scale, datapoint.x0, datapoint.x1, relAccessor);
}

export function yAccessor(
  scale: AnyXYChartScale,
  relAccessor?: (value: number) => number,
) {
  return (datapoint: RectDatapointInternal) =>
    genericAccessor(scale, datapoint.y0, datapoint.y1, relAccessor);
}

export function widthAccessor(
  scale: AnyXYChartScale,
  relAccessor?: (value: number) => number,
) {
  return (datapoint: RectDatapointInternal) =>
    spanAccessor(scale, datapoint.x0, datapoint.x1, relAccessor);
}

export function heightAccessor(
  scale: AnyXYChartScale,
  relAccessor?: (value: number) => number,
) {
  return (datapoint: RectDatapointInternal) =>
    spanAccessor(scale, datapoint.y0, datapoint.y1, relAccessor);
}
